import { ReactNode, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Menu, X } from "lucide-react";
import { FaApple, FaGooglePlay } from "react-icons/fa";
import { APP_STORE_LINK, GOOGLE_PLAY_LINK } from "../constants";
import HomeFooter from "./home/HomeFooter";
import PageMetadata from "./PageMetadata";
import "../styles/homepage.css";
import "../styles/public-pages.css";

export const imageFallback = "/images/logo/main-logo.png";
export const postImagePlaceholder = "/images/image-placeholder.svg";

export function PublicHeader() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const close = (event: KeyboardEvent) => { if (event.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, []);
  return <>
    <header className="nav is-scrolled detail-nav">
      <div className="wrap nav__inner">
        <Link to="/" className="nav__logo" aria-label="CityMoshi home"><img src="/homepage/img/logo.png" alt="CityMoshi" width="150" height="38" /></Link>
        <nav className="nav__links" aria-label="Main navigation">
          <Link to="/#discover">Discover</Link><Link to="/#social">The app</Link><Link to="/#organizers">Organizers</Link>
        </nav>
        <div className="nav__actions">
          <a className="btn btn--ghost btn--sm detail-host" href="https://dashboard.citymoshi.com" target="_blank" rel="noreferrer">Host an event</a>
          <a className="btn btn--primary btn--sm detail-app-link" href="#download">Get the app</a>
          <button className="nav__burger" aria-label={open ? "Close menu" : "Open menu"} aria-expanded={open} aria-controls="detail-mobile-menu" onClick={() => setOpen(!open)}>{open ? <X /> : <Menu />}</button>
        </div>
      </div>
    </header>
    {open && <nav id="detail-mobile-menu" className="home-mobile-menu" aria-label="Mobile navigation">
      {[["/#discover", "Discover"], ["/#social", "The app"], ["/#organizers", "Organizers"]].map(([to, label]) => <Link key={to} to={to} onClick={() => setOpen(false)}>{label}<ArrowUpRight /></Link>)}
    </nav>}
  </>;
}

export function PublicShell({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <div className={`homepage detail-page ${className}`}>
    <PublicHeader />{children}<HomeFooter homePath="/" />
  </div>;
}

export function DownloadAppCard() {
  return <section className="detail-download" id="download" aria-labelledby="detail-download-title">
    <div><p className="eyebrow">GET THE APP</p><h2 id="detail-download-title">Your city, in<br />your <em>pocket.</em></h2>
      <p className="lede">Discover events, follow people, and share your moments from the CityMoshi app.</p>
      <div className="stores">
        <a className="store" href={APP_STORE_LINK} target="_blank" rel="noreferrer"><FaApple /><span><small>Download on the</small><b>App Store</b></span></a>
        <a className="store" href={GOOGLE_PLAY_LINK} target="_blank" rel="noreferrer"><FaGooglePlay /><span><small>Get it on</small><b>Google Play</b></span></a>
      </div>
    </div>
    <div className="detail-download-art" aria-hidden="true"><img src="/homepage/img/phone-feed.webp" alt="" loading="lazy" /><img src="/homepage/img/phone-map.webp" alt="" loading="lazy" /></div>
  </section>;
}

export function PageLoading() {
  return <PublicShell><main className="wrap detail-state" aria-busy="true"><div className="ev-skel ev-skel--hero" /><div className="ev-skel ev-skel--line" /><p role="status">Loading CityMoshi…</p></main></PublicShell>;
}
export function PageError({ message = "We couldn't find this page." }: { message?: string }) {
  const { pathname } = useLocation();
  return <PublicShell><PageMetadata title="Page unavailable" description={message} url={pathname} /><main className="wrap detail-state"><p className="eyebrow">CITYMOSHI</p><h1>This page isn't available.</h1><p>{message}</p><Link className="btn btn--primary" to="/">Back to CityMoshi <ArrowUpRight /></Link><DownloadAppCard /></main></PublicShell>;
}

export function EventCard({ event }: { event: any }) {
  return <Link to={event.slug ? `/e/${event.slug}` : `/events/${event.id}`} className="detail-event-card">
    <div className="detail-event-image"><img src={event.image || imageFallback} alt="" loading="lazy" onError={e => { e.currentTarget.onerror = null; e.currentTarget.src = imageFallback; }} /><span>{new Date(event.startDate).toLocaleDateString("en", { month: "short", day: "numeric" })}</span></div>
    <div><p className="eyebrow">{event.category?.name || "Event"}</p><h3>{event.name}</h3><p>{event.location?.name || event.location?.address || "CityMoshi"}</p><span className="detail-event-go">View event <ArrowUpRight /></span></div>
  </Link>;
}
export function Avatar({ src, name, large = false }: { src?: string | null; name: string; large?: boolean }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  return src && !failed ? <img className={`avatar ${large ? "avatar-large" : ""}`} src={src} alt="" onError={() => setFailed(true)} /> : <span className={`avatar avatar-fallback ${large ? "avatar-large" : ""}`}>{name.slice(0, 1)}</span>;
}
export function BackToEvents() {
  return <Link className="po__back" to="/#discover"><ArrowLeft /> Back to events</Link>;
}
